import { useState, useEffect } from "react";
import "@/App.css";
import { Phone, Mail, MapPin, Clock, Shield, Award, Star, CheckCircle, ArrowRight, Menu, X, ChevronDown, Wrench, Home as HomeIcon, Droplets, AlertTriangle, Users, Calendar } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Assets
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_premiumroofs/artifacts/5dnmb59e_LP%20EXCEL%20LOGO-3.png";
const COMPANY_VIDEO = "https://customer-assets.emergentagent.com/job_midwest-roofing/artifacts/z75z5fvp_62335F50-7CCC-44DA-9FA7-7168E048061C.mov";

// Professional Portfolio Images
const PORTFOLIO_IMAGES = {
  roofReplacement: "https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800",
  stormDamage: "https://images.unsplash.com/photo-1632759145351-1d592919f522?w=800",
  commercialRoof: "https://images.pexels.com/photos/31763537/pexels-photo-31763537.jpeg?auto=compress&cs=tinysrgb&w=800"
};

// Navigation Component
const Navigation = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Services", href: "#services" },
    { name: "Portfolio", href: "#portfolio" },
    { name: "Reviews", href: "#reviews" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'}`}>
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-red-500" />
              Licensed & Insured
            </span>
            <span className="flex items-center gap-2">
              <Award className="w-4 h-4 text-red-500" />
              25+ Years Experience
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-red-500" />
              24/7 Emergency Service
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="mailto:info@lpdecorinc.com" className="hover:text-red-400 transition-colors">
              info@lpdecorinc.com
            </a>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Logo - Large and clear with white background for transparency effect */}
          <a href="#home" className="flex items-center">
            <div className="bg-white rounded-lg">
              <img 
                src={COMPANY_LOGO} 
                alt="LP DECOR, INC." 
                className="h-16 md:h-20 w-auto object-contain mix-blend-multiply"
              />
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-slate-700 hover:text-red-600 font-medium transition-colors"
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:6304453086" className="flex items-center gap-2 text-slate-900">
              <Phone className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-xs text-slate-500">Call Now</p>
                <p className="font-bold">(630) 445-3086</p>
              </div>
            </a>
            <a
              href="#contact"
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Free Estimate
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t pt-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block py-3 text-slate-700 hover:text-red-600 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a
              href="tel:6304453086"
              className="mt-4 flex items-center gap-2 bg-red-600 text-white px-4 py-3 rounded-lg font-semibold justify-center"
            >
              <Phone className="w-5 h-5" />
              Call (630) 445-3086
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="relative pt-32 md:pt-40 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="flex items-center gap-2 text-red-500 font-semibold mb-4 animate-fade-in-up">
              <Star className="w-5 h-5 fill-red-500 animate-pulse" />
              <span className="font-bold text-red-500">
                #1 Rated Roofing Company in Chicago
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              <span className="inline-block animate-slide-in-left">Expert Roofing</span>
              <span className="text-red-500 inline-block animate-slide-in-right animation-delay-200"> Solutions</span>
              <span className="inline-block animate-slide-in-up animation-delay-400"> You Can Trust</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed animate-fade-in animation-delay-600">
              Licensed, insured, and trusted by thousands of homeowners across the Midwest. 
              Get your roof fixed right the first time with our 25+ years of experience.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white text-sm">Fully Insured</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Award className="w-5 h-5 text-yellow-400" />
                <span className="text-white text-sm">GAF Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                <span className="text-white text-sm">Free Estimates</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="tel:6304453086"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                Call (630) 445-3086
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105"
              >
                Get Free Estimate
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            {/* Service Areas */}
            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-red-500 font-semibold text-sm mb-2">PROUDLY SERVING</p>
              <p className="text-white">Chicago • Wisconsin • Iowa • Nebraska • Indiana • Colorado</p>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Get Your Free Estimate</h3>
            <p className="text-slate-600 mb-6">No obligation. No high-pressure sales.</p>
            <ContactForm compact />
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { number: "25+", label: "Years Experience" },
    { number: "5,000+", label: "Projects Completed" },
    { number: "100%", label: "Satisfaction Rate" },
    { number: "24/7", label: "Emergency Service" },
  ];

  return (
    <section className="py-12 bg-white border-b">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-red-600 mb-2">{stat.number}</p>
              <p className="text-slate-600 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const features = [
    {
      icon: Shield,
      title: "Licensed & Insured",
      description: "Fully licensed and insured for your complete protection and peace of mind."
    },
    {
      icon: Award,
      title: "Certified Contractors",
      description: "GAF Master Elite and manufacturer certified for superior quality work."
    },
    {
      icon: Clock,
      title: "Fast Response",
      description: "Same-day service for emergency situations. We're here when you need us."
    },
    {
      icon: Users,
      title: "Local Family Business",
      description: "Serving our community with pride and dedication for over two decades."
    }
  ];

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Why Choose LP DECOR?</h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            We're committed to providing the highest quality roofing services with integrity and professionalism.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <feature.icon className="w-7 h-7 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
              <p className="text-slate-600">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Certifications */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-sm">
          <h3 className="text-center text-lg font-semibold text-slate-900 mb-8">Certified by Leading Manufacturers</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "GAF", rating: "Master Elite", subtitle: "Certified Contractor" },
              { name: "Owens Corning", rating: "Preferred", subtitle: "Contractor" },
              { name: "CertainTeed", rating: "Select", subtitle: "Shingle Master" }
            ].map((cert, index) => (
              <div key={index} className="text-center p-4 border rounded-lg">
                <p className="font-bold text-slate-900">{cert.name}</p>
                <p className="text-red-600 font-semibold">{cert.rating}</p>
                <p className="text-sm text-slate-500">{cert.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Wrench,
      title: "Roofing",
      description: "Complete roofing services including repair, replacement, and new installation for residential and commercial properties.",
      features: ["Roof Repair & Replacement", "Asphalt Shingles", "Metal Roofing", "Flat Roof Systems", "Tile & Slate Roofing"],
      popular: true
    },
    {
      icon: HomeIcon,
      title: "Siding",
      description: "Professional siding installation and repair to enhance your home's curb appeal and protection.",
      features: ["Vinyl Siding", "Fiber Cement Siding", "Wood Siding", "Insulated Siding", "Siding Repair"],
      popular: false
    },
    {
      icon: HomeIcon,
      title: "Windows",
      description: "Energy-efficient window installation and replacement for better comfort and lower energy bills.",
      features: ["Window Replacement", "Energy-Efficient Windows", "Bay & Bow Windows", "Double & Triple Pane", "Window Repair"],
      popular: true
    },
    {
      icon: HomeIcon,
      title: "Skylights",
      description: "Brighten your home with professional skylight installation, repair, and replacement.",
      features: ["Skylight Installation", "Skylight Replacement", "Tubular Skylights", "Vented Skylights", "Leak Repair"],
      popular: false
    },
    {
      icon: Droplets,
      title: "Gutters",
      description: "Complete gutter installation, repair, and maintenance to protect your foundation.",
      features: ["Seamless Gutters", "Gutter Installation", "Gutter Guards", "Downspout Repair", "Gutter Cleaning"],
      popular: false
    },
    {
      icon: HomeIcon,
      title: "Fascia & Soffit",
      description: "Professional fascia and soffit installation and repair for a finished, protected roofline.",
      features: ["Fascia Installation", "Soffit Installation", "Aluminum Fascia", "Vinyl Soffit", "Wood Rot Repair"],
      popular: false
    },
    {
      icon: AlertTriangle,
      title: "Storm Damage",
      description: "Comprehensive storm damage assessment, insurance claims help, and restoration services.",
      features: ["Insurance Claims Help", "Emergency Response", "Hail Damage Repair", "Wind Damage Repair", "Full Restoration"],
      popular: true
    },
    {
      icon: Clock,
      title: "Emergency Services",
      description: "24/7 emergency services for urgent repairs and temporary protection.",
      features: ["24/7 Availability", "Rapid Response", "Emergency Tarping", "Board-Up Services", "Temporary Repairs"],
      popular: false
    }
  ];

  const chicagolandSuburbs = [
    "Chicago", "Naperville", "Aurora", "Joliet", "Elgin", "Waukegan",
    "Cicero", "Arlington Heights", "Evanston", "Schaumburg", "Bolingbrook",
    "Palatine", "Skokie", "Des Plaines", "Orland Park", "Tinley Park",
    "Oak Lawn", "Berwyn", "Mount Prospect", "Wheaton", "Hoffman Estates",
    "Oak Park", "Downers Grove", "Elmhurst", "Glenview", "Lombard",
    "Buffalo Grove", "Crystal Lake", "Streamwood", "Carol Stream", "Romeoville",
    "Hanover Park", "Carpentersville", "Wheeling", "Park Ridge", "Addison",
    "Calumet City", "Elk Grove Village", "Woodridge", "St. Charles", "Geneva",
    "Batavia", "Glen Ellyn", "Bartlett", "Lisle", "Westmont", "Oak Brook",
    "Hinsdale", "Clarendon Hills", "Western Springs", "La Grange"
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Complete Exterior Services</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Roofing, siding, windows, skylights, gutters, fascia & soffit installation throughout Chicagoland.
            Licensed, insured, and trusted by thousands of homeowners.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div key={index} className={`relative bg-white rounded-xl border-2 p-5 hover:shadow-xl transition-all ${service.popular ? 'border-red-500' : 'border-slate-200 hover:border-red-300'}`}>
              {service.popular && (
                <span className="absolute -top-3 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  Popular
                </span>
              )}
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-3">
                <service.icon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{service.title}</h3>
              <p className="text-slate-600 text-sm mb-3">{service.description}</p>
              <ul className="space-y-1 mb-4">
                {service.features.slice(0, 4).map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-700 text-sm">
                    <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                className="inline-flex items-center gap-1 text-red-600 font-semibold text-sm hover:text-red-700 transition-colors"
              >
                Free Estimate
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>

        {/* Chicagoland Service Areas */}
        <div className="mt-16 bg-slate-50 rounded-2xl p-8">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Serving All Chicagoland Suburbs</h3>
            <p className="text-slate-600">Professional roofing, siding, windows & exterior services throughout the Chicago metropolitan area</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {chicagolandSuburbs.map((suburb, index) => (
              <span key={index} className="bg-white px-3 py-1 rounded-full text-sm text-slate-700 border border-slate-200 hover:border-red-300 hover:text-red-600 transition-colors cursor-default">
                {suburb}
              </span>
            ))}
          </div>
          <p className="text-center mt-4 text-slate-500 text-sm">+ Wisconsin, Iowa, Nebraska, Indiana & Colorado</p>
        </div>

        {/* Emergency CTA */}
        <div className="mt-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 md:p-12 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">Need Emergency Service?</h3>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            We provide 24/7 emergency roofing services throughout Chicagoland. Don't wait – protect your property now!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:6304453086"
              className="inline-flex items-center justify-center gap-2 bg-white text-red-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-100 transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call (630) 445-3086
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition-colors"
            >
              Request Service Online
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const projects = [
    {
      image: PORTFOLIO_IMAGES.roofReplacement,
      title: "Complete Roof Replacement",
      location: "Naperville, IL",
      description: "Full residential roof replacement featuring premium architectural shingles."
    },
    {
      image: PORTFOLIO_IMAGES.stormDamage,
      title: "Storm Damage Restoration",
      location: "Oak Brook, IL",
      description: "Emergency storm damage repair and complete roof restoration."
    },
    {
      image: PORTFOLIO_IMAGES.commercialRoof,
      title: "Commercial Roofing Project",
      location: "Wheaton, IL",
      description: "Large-scale commercial roofing installation with weatherproofing."
    }
  ];

  return (
    <section id="portfolio" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Project Portfolio</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            See the quality of our work through completed projects across Chicago and the Midwest.
          </p>
        </div>

        {/* Featured Video Section */}
        <div className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="relative">
              <video 
                className="w-full h-[400px] md:h-[500px] object-cover"
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={COMPANY_VIDEO} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <div className="text-white">
                  <span className="inline-block bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-3">
                    Featured Project
                  </span>
                  <h3 className="text-2xl font-bold mb-2">Our Work in Action</h3>
                  <p className="text-gray-200">Watch our professional team deliver quality roofing services.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div key={index} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow">
              <div className="relative h-64 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                  <MapPin className="w-4 h-4" />
                  {project.location}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{project.title}</h3>
                <p className="text-slate-600">{project.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors"
          >
            Start Your Project
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "LP DECOR did an amazing job on our roof replacement. The crew was professional, clean, and finished on time. Highly recommend!",
      author: "Sarah Johnson",
      location: "Naperville, IL",
      service: "Roof Replacement"
    },
    {
      quote: "After a bad storm damaged our roof, LP DECOR responded immediately. They handled everything with our insurance and made the process stress-free.",
      author: "Michael Chen",
      location: "Oak Brook, IL",
      service: "Storm Damage"
    },
    {
      quote: "Best roofing company in the Chicago area! Fair pricing, excellent communication, and top-quality work. Our new roof looks fantastic.",
      author: "Jennifer Martinez",
      location: "Wheaton, IL",
      service: "Roof Installation"
    }
  ];

  return (
    <section id="reviews" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">What Our Customers Say</h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Don't just take our word for it – see what thousands of satisfied customers have to say.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto mb-12">
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">4.9/5</p>
            <p className="text-slate-600">Average Rating</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">2,500+</p>
            <p className="text-slate-600">Happy Customers</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-red-600">98%</p>
            <p className="text-slate-600">Would Recommend</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-slate-50 rounded-xl p-6">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-slate-700 mb-6 italic">"{testimonial.quote}"</p>
              <div>
                <p className="font-bold text-slate-900">{testimonial.author}</p>
                <p className="text-sm text-slate-500">{testimonial.location}</p>
                <span className="inline-block mt-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                  {testimonial.service}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Form Component
const ContactForm = ({ compact = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSubmitted(true);
    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h4 className="text-xl font-bold text-slate-900 mb-2">Thank You!</h4>
        <p className="text-slate-600">We'll contact you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <input
          type="text"
          placeholder="Your Name *"
          required
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          type="email"
          placeholder="Email Address *"
          required
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
        />
        <input
          type="tel"
          placeholder="Phone Number *"
          required
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
        />
      </div>
      <div>
        <select
          required
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-slate-700"
          value={formData.service}
          onChange={(e) => setFormData({...formData, service: e.target.value})}
        >
          <option value="">Select Service Needed *</option>
          <option value="roof-repair">Roof Repair</option>
          <option value="roof-replacement">Roof Replacement</option>
          <option value="siding">Siding Installation</option>
          <option value="gutters">Gutter Services</option>
          <option value="storm-damage">Storm Damage</option>
          <option value="emergency">Emergency Service</option>
        </select>
      </div>
      {!compact && (
        <div>
          <textarea
            placeholder="Tell us about your project..."
            rows={4}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            value={formData.message}
            onChange={(e) => setFormData({...formData, message: e.target.value})}
          />
        </div>
      )}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white py-4 rounded-lg font-bold text-lg transition-colors"
      >
        {isSubmitting ? "Submitting..." : "Get My Free Estimate"}
      </button>
      <p className="text-xs text-slate-500 text-center">
        By submitting this form, you agree to be contacted by LP DECOR, INC. regarding your roofing needs.
      </p>
    </form>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-20 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Form */}
          <div className="bg-white rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Get Your Free Estimate</h2>
            <p className="text-slate-600 mb-8">
              Ready to get started? Contact us today for a free, no-obligation estimate.
            </p>
            <ContactForm />
          </div>

          {/* Right - Contact Info */}
          <div className="text-white">
            <h3 className="text-2xl font-bold mb-8">Contact Information</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-lg">Call Now</p>
                  <a href="tel:6304453086" className="text-2xl font-bold text-red-500 hover:text-red-400">
                    (630) 445-3086
                  </a>
                  <p className="text-slate-400 text-sm mt-1">Available 24/7 for emergencies</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-lg">Email Us</p>
                  <a href="mailto:info@lpdecorinc.com" className="text-red-500 hover:text-red-400">
                    info@lpdecorinc.com
                  </a>
                  <p className="text-slate-400 text-sm mt-1">We respond within 2 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-lg">Business Hours</p>
                  <p className="text-slate-300">Mon - Fri: 7:00 AM - 7:00 PM</p>
                  <p className="text-slate-300">Saturday: 8:00 AM - 5:00 PM</p>
                  <p className="text-slate-300">Sunday: Emergency Only</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-semibold text-lg">Service Areas</p>
                  <p className="text-slate-300">Chicago • Wisconsin • Iowa</p>
                  <p className="text-slate-300">Nebraska • Indiana • Colorado</p>
                </div>
              </div>
            </div>

            {/* Guarantees */}
            <div className="mt-12 p-6 bg-white/10 rounded-xl">
              <h4 className="font-bold text-lg mb-4">Our Guarantees</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>10-Year Workmanship Warranty</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>Lifetime Material Warranties Available</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>100% Satisfaction Promise</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: "How much does a roof replacement cost?",
      answer: "Roof replacement costs vary based on size, materials, and complexity. We provide free, detailed estimates with transparent pricing. Most residential roofs range from $8,000-$25,000 depending on these factors."
    },
    {
      question: "Do you work with insurance companies?",
      answer: "Yes! We have extensive experience working with all major insurance companies. We'll help document the damage, meet with adjusters, and ensure you get fair compensation for covered repairs."
    },
    {
      question: "How long does a roof installation take?",
      answer: "Most residential roof replacements are completed in 1-3 days, depending on size and weather conditions. We'll provide a specific timeline during your estimate."
    },
    {
      question: "What warranties do you offer?",
      answer: "We offer a 10-year workmanship warranty on all installations. Additionally, manufacturer warranties on materials can extend up to a lifetime depending on the products selected."
    },
    {
      question: "Do you provide emergency services?",
      answer: "Yes! We offer 24/7 emergency roofing services. Call us anytime at (630) 445-3086 for emergency tarping, temporary repairs, and urgent assistance."
    }
  ];

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-slate-600">
            Get answers to common questions about our roofing services.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <button
                className="w-full px-6 py-4 flex items-center justify-between text-left"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              >
                <span className="font-semibold text-slate-900">{faq.question}</span>
                <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4">
                  <p className="text-slate-600">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">Still have questions?</p>
          <a
            href="tel:6304453086"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            <Phone className="w-5 h-5" />
            Call (630) 445-3086
          </a>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-950 text-white py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo & About */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={COMPANY_LOGO} 
                alt="LP DECOR, INC." 
                className="h-16 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 mb-4">
              Chicago's trusted roofing contractor with over 25 years of experience. 
              Licensed, insured, and committed to quality.
            </p>
            <div className="flex items-center gap-4">
              <a href="tel:6304453086" className="text-red-500 hover:text-red-400 font-semibold">
                (630) 445-3086
              </a>
              <span className="text-slate-600">|</span>
              <a href="mailto:info@lpdecorinc.com" className="text-red-500 hover:text-red-400">
                info@lpdecorinc.com
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold mb-4">Services</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#services" className="hover:text-white">Roof Repair</a></li>
              <li><a href="#services" className="hover:text-white">Roof Replacement</a></li>
              <li><a href="#services" className="hover:text-white">Siding Installation</a></li>
              <li><a href="#services" className="hover:text-white">Gutter Services</a></li>
              <li><a href="#services" className="hover:text-white">Storm Damage</a></li>
              <li><a href="#services" className="hover:text-white">Emergency Services</a></li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-bold mb-4">Service Areas</h4>
            <ul className="space-y-2 text-slate-400">
              <li>Chicago, IL</li>
              <li>Wisconsin</li>
              <li>Iowa</li>
              <li>Nebraska</li>
              <li>Indiana</li>
              <li>Colorado</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} LP DECOR, INC. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <span>Licensed & Insured</span>
            <span>•</span>
            <span>GAF Certified</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App min-h-screen">
      <Navigation scrolled={scrolled} />
      <HeroSection />
      <StatsSection />
      <WhyChooseUsSection />
      <ServicesSection />
      <PortfolioSection />
      <TestimonialsSection />
      <ContactSection />
      <FAQSection />
      <Footer />
    </div>
  );
}

export default App;
